import { useCallback, useMemo } from "react";
import {
  Box,
  Button,
  Checkbox,
  Group,
  Modal,
  MultiSelect,
  Select,
  Stack,
  TagsInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useSourceSubsitesBySourceId } from "../../../hooks/admin/useSourceSubsites";
import type { SourceDefaultStain } from "../../../services/admin/sourcedefaultstains.service";
import { required, scrollToFirstError } from "../../../utils/formValidation";

export interface SourceDefaultStainFormValues {
  sourceId: string;
  stainId: string;
  gStainId: string;
  findingsTriggers: string[];
  findingsIgnoreTriggers: string[];
  excludeSubsiteIds: string[];
  excludeWhenNoSubsite: boolean;
}

interface Option {
  value: string;
  label: string;
}

interface Props {
  opened: boolean;
  editing: SourceDefaultStain | null;
  sourceOptions: Option[];
  stainOptions: Option[];
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (values: SourceDefaultStainFormValues) => Promise<void>;
}

const EMPTY_VALUES: SourceDefaultStainFormValues = {
  sourceId: "",
  stainId: "",
  gStainId: "",
  findingsTriggers: [],
  findingsIgnoreTriggers: [],
  excludeSubsiteIds: [],
  excludeWhenNoSubsite: false,
};

function getInitialValues(
  editing: SourceDefaultStain | null
): SourceDefaultStainFormValues {
  if (!editing) return { ...EMPTY_VALUES };
  return {
    sourceId: editing.sourceId,
    stainId: editing.stainId,
    gStainId: editing.gStainId || "",
    findingsTriggers: editing.findingsTriggers || [],
    findingsIgnoreTriggers: editing.findingsIgnoreTriggers || [],
    excludeSubsiteIds: editing.excludeSubsiteIds || [],
    excludeWhenNoSubsite: editing.excludeWhenNoSubsite ?? false,
  };
}

export function SourceDefaultStainFormModal({
  opened,
  editing,
  sourceOptions,
  stainOptions,
  isSubmitting,
  onClose,
  onSubmit,
}: Props) {
  const form = useForm<SourceDefaultStainFormValues>({
    initialValues: getInitialValues(editing),
    validate: {
      sourceId: required("Source is required"),
      stainId: required("Stain is required"),
    },
  });
  const { data: sourceSubsites = [] } = useSourceSubsitesBySourceId(
    form.values.sourceId
  );
  const subsiteOptions = useMemo(
    () =>
      sourceSubsites.map((subsite: any) => ({
        value: subsite.id,
        label: subsite.name || subsite.id,
      })),
    [sourceSubsites]
  );

  const handleValidSubmit = useCallback(
    async (values: SourceDefaultStainFormValues) => {
      await onSubmit(values);
    },
    [onSubmit]
  );
  const handleInvalidSubmit = useCallback(() => {
    scrollToFirstError(form);
  }, [form]);
  const handleSubmit = form.onSubmit(handleValidSubmit, handleInvalidSubmit);

  return (
    <Modal.Root
      opened={opened}
      onClose={onClose}
      closeOnClickOutside={false}
      size="lg"
    >
      <Modal.Overlay />
      <Modal.Content
        style={{ display: "flex", flexDirection: "column", maxHeight: "90vh" }}
      >
        <Modal.Header>
          <Modal.Title>
            {editing ? "Edit source default stain" : "Add source default stain"}
          </Modal.Title>
          <Modal.CloseButton />
        </Modal.Header>
        <Modal.Body style={{ overflowY: "auto" }}>
          <Box
            component="form"
            id="source-default-stain-form"
            onSubmit={handleSubmit}
          >
            <Stack gap="md">
              <Select
                label="Source"
                placeholder="Select a source"
                data={sourceOptions}
                searchable
                withAsterisk
                {...form.getInputProps("sourceId")}
              />
              <Select
                label="Stain"
                placeholder="Select a stain"
                data={stainOptions}
                searchable
                withAsterisk
                {...form.getInputProps("stainId")}
              />
              <Select
                label="G stain version"
                description="For G-prefix cases, use this stain instead when this source-default rule matches."
                placeholder="Use the standard stain"
                data={stainOptions}
                searchable
                clearable
                {...form.getInputProps("gStainId")}
              />
              <TagsInput
                label="Findings triggers"
                description="Keywords that must appear in specimen findings. Leave empty to always assign."
                placeholder="Type a keyword and press Enter"
                {...form.getInputProps("findingsTriggers")}
              />
              <TagsInput
                label="Findings ignore triggers"
                description="Keywords that prevent this stain from being assigned."
                placeholder="Type a keyword and press Enter"
                {...form.getInputProps("findingsIgnoreTriggers")}
              />
              <MultiSelect
                label="Exclude subsites"
                description="Do not assign this stain when the specimen has one of these subsites."
                placeholder={
                  form.values.sourceId
                    ? "Select subsites to exclude"
                    : "Select a source first"
                }
                data={subsiteOptions}
                searchable
                clearable
                disabled={!form.values.sourceId || subsiteOptions.length === 0}
                {...form.getInputProps("excludeSubsiteIds")}
              />
              <Checkbox
                label="Exclude when no subsite is selected"
                description="Do not assign this stain when the specimen has no subsite selected."
                {...form.getInputProps("excludeWhenNoSubsite", {
                  type: "checkbox",
                })}
              />
            </Stack>
          </Box>
        </Modal.Body>
        <Group
          justify="space-between"
          p="md"
          style={{ borderTop: "1px solid var(--mantine-color-default-border)" }}
        >
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="source-default-stain-form"
            loading={isSubmitting}
          >
            {editing ? "Save changes" : "Create source default stain"}
          </Button>
        </Group>
      </Modal.Content>
    </Modal.Root>
  );
}
